// Live extension: one Chainlink round (JSON-RPC eth_call) and one DexScreener pool price per poll, as a row
// appended to the drawn series. Endpoints are parameters; nothing here reads window globals.
import type { ChartSymbol } from "../api/types";
import type { SeriesRow } from "./types";

export const DATA_URL = "data/chart.json";
export const API_PATH = "/api/data/chart?hours=72";
export const API_EVERY_MS = 120000;
export const RPCS = ["https://robinhood-rpc.publicnode.com", "https://robinhood.drpc.org"];
export const RPC_PROXY = "/api/rpc";
export const SEL = { latestRoundData: "0xfeaf968c", decimals: "0x313ce567" } as const;
export const LIVE_EVERY_MS = 60000;

export type ProxyState = "unknown" | "ok" | "no";

export interface RpcOptions {
  /** Relative proxy tried first; null disables the proxy. */
  proxy?: string | null | undefined;
  rpcs?: string[] | undefined;
  fetch?: typeof fetch | undefined;
}

export interface RpcClient {
  rpc(method: string, params: unknown[]): Promise<unknown>;
  call(to: string, data: string): Promise<string>;
  readonly proxyState: ProxyState;
}

export const isLocalHost = (hostname: string): boolean => /^(localhost|127\.0\.0\.1|\[::1\])$/.test(hostname);

/**
 * The proxy to try: on localhost only an explicit override (so a plain static server does not log a failed POST
 * on every page load), elsewhere the override or /api/rpc.
 */
export function defaultRpcProxy(hostname: string, override?: string | null): string | null {
  if (override !== undefined) return override;
  return isLocalHost(hostname) ? null : RPC_PROXY;
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null;
const errorText = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** JSON-RPC over the proxy while it answers, then the public RPCs in order. */
export function createRpcClient(opts: RpcOptions = {}): RpcClient {
  const doFetch = opts.fetch ?? fetch;
  const rpcs = opts.rpcs ?? RPCS;
  const proxy = opts.proxy ?? null;
  let proxyState: ProxyState = proxy ? "unknown" : "no";
  async function rpcOnce(url: string, method: string, params: unknown[]): Promise<unknown> {
    const r = await doFetch(url, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }) });
    if (!r.ok) throw new Error(`http ${r.status}`);
    const j: unknown = await r.json();
    if (!isRecord(j)) throw new Error("no result");
    if (j.error !== undefined && j.error !== null) {
      const msg = isRecord(j.error) && typeof j.error.message === "string" ? j.error.message : "rpc error";
      throw new Error(msg);
    }
    if (j.result === undefined) throw new Error("no result");
    return j.result;
  }
  async function rpc(method: string, params: unknown[]): Promise<unknown> {
    let err: unknown;
    if (proxyState !== "no" && proxy) {
      try {
        const res = await rpcOnce(proxy, method, params);
        proxyState = "ok";
        return res;
      } catch (e: unknown) {
        if (proxyState === "unknown") proxyState = "no";
        err = e;
      }
    }
    for (const url of rpcs) {
      try {
        return await rpcOnce(url, method, params);
      } catch (e: unknown) {
        err = e;
      }
    }
    throw err instanceof Error ? err : new Error("no rpc");
  }
  async function call(to: string, data: string): Promise<string> {
    const res = await rpc("eth_call", [{ to, data }, "latest"]);
    if (typeof res !== "string") throw new Error("no result");
    return res;
  }
  return { rpc, call, get proxyState() { return proxyState; } };
}

export interface FeedReading {
  price: number;
  /** ms */
  updatedAt: number;
}
export interface PoolReading {
  price: number;
  pair: string;
}

const decimalsCache: Record<string, number> = {};

/** latestRoundData of a Chainlink aggregator, scaled by its decimals (cached per feed). */
export async function readFeed(client: RpcClient, feed: string): Promise<FeedReading> {
  let decimals = decimalsCache[feed];
  if (decimals === undefined) {
    decimals = Number(BigInt(await client.call(feed, SEL.decimals)));
    decimalsCache[feed] = decimals;
  }
  const raw = await client.call(feed, SEL.latestRoundData);
  const b = raw.slice(2);
  const answer = BigInt("0x" + b.slice(64, 128));
  const updatedAt = Number(BigInt("0x" + b.slice(192, 256)));
  return { price: Number(answer) / 10 ** decimals, updatedAt: updatedAt * 1000 };
}

interface DexPair {
  pairAddress: string;
  priceUsd: string;
  liquidityUsd: number;
  chainId: string;
}
function dexPairs(v: unknown): DexPair[] {
  if (!Array.isArray(v)) return [];
  const out: DexPair[] = [];
  for (const p of v as unknown[]) {
    if (!isRecord(p)) continue;
    const liq = isRecord(p.liquidity) && typeof p.liquidity.usd === "number" ? p.liquidity.usd : 0;
    out.push({
      pairAddress: typeof p.pairAddress === "string" ? p.pairAddress : "",
      priceUsd: typeof p.priceUsd === "string" ? p.priceUsd : "",
      liquidityUsd: liq,
      chainId: typeof p.chainId === "string" ? p.chainId : "",
    });
  }
  return out;
}

/** The pool price from DexScreener: the entry's pair when listed, else the most liquid pair of the token. */
export async function readPool(entry: Pick<ChartSymbol, "pair" | "token">, doFetch: typeof fetch = fetch): Promise<PoolReading> {
  const pick = (list: DexPair[]): PoolReading => {
    const want = (entry.pair ?? "").toLowerCase();
    const hit = list.find((p) => p.pairAddress.toLowerCase() === want) ?? list.sort((a, b) => b.liquidityUsd - a.liquidityUsd)[0];
    if (!hit?.priceUsd) throw new Error("no pair");
    return { price: Number(hit.priceUsd), pair: hit.pairAddress };
  };
  try {
    const r = await doFetch(`https://api.dexscreener.com/latest/dex/pairs/robinhood/${entry.pair ?? ""}`);
    const j: unknown = await r.json();
    const list = isRecord(j) ? (Array.isArray(j.pairs) ? j.pairs : j.pair ? [j.pair] : []) : [];
    return pick(dexPairs(list));
  } catch {
    const r = await doFetch(`https://api.dexscreener.com/token-pairs/v1/robinhood/${entry.token ?? ""}`);
    const j: unknown = await r.json();
    const list = Array.isArray(j) ? j : isRecord(j) && Array.isArray(j.pairs) ? j.pairs : [];
    return pick(dexPairs(list).filter((p) => p.chainId === "robinhood"));
  }
}

export interface LiveReading {
  symbol: string;
  feed: FeedReading | null;
  pool: PoolReading | null;
  err: string;
  /** ms, when the poll finished */
  at: number;
}

/** One poll of both sources. Failures are reported in `err`, never thrown. */
export async function pollLive(client: RpcClient, symbol: string, entry: ChartSymbol, doFetch: typeof fetch = fetch): Promise<LiveReading> {
  let feed: FeedReading | null = null, pool: PoolReading | null = null, err = "";
  try {
    if (!entry.feed) throw new Error("no feed address");
    feed = await readFeed(client, entry.feed);
  } catch (x: unknown) {
    err = "feed: " + errorText(x);
  }
  try {
    pool = await readPool(entry, doFetch);
  } catch (x: unknown) {
    err += (err ? "; " : "") + "pool: " + errorText(x);
  }
  return { symbol, feed, pool, err, at: Date.now() };
}

/** The series row of a complete reading (both sources answered), else null. */
export function liveRow(reading: LiveReading): SeriesRow | null {
  const { feed, pool, at } = reading;
  if (!feed || !pool) return null;
  const oracle = feed.price;
  return { t: at, oracle, updatedAt: feed.updatedAt, pool: pool.price, basis: (pool.price / oracle - 1) * 100, src: "live" };
}
